import dayjs, { type Dayjs, type ManipulateType } from 'dayjs';
import 'dayjs/locale/ru';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isoWeek from 'dayjs/plugin/isoWeek';
import minMax from 'dayjs/plugin/minMax';
import utc from 'dayjs/plugin/utc';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(duration);
dayjs.extend(isoWeek);
dayjs.extend(minMax);
dayjs.locale('ru');

export const shortDateFormat = 'D MMM YY';
export const formattedDateFormat = 'DD.MM.YYYY';
export const isoFormatDateFormat = 'YYYY-MM-DD';

export const increaseDate = (date: Date | Dayjs | string, add: number, period: ManipulateType = 'days') =>
  dayjs(date).utc().add(add, period);

export const getIsoDateFormat = (date: Date | string | Dayjs, format?: string) =>
  dayjs(date, format).format(isoFormatDateFormat);

export const isLessDate = (firstDate: Date | string, secondDate: Date | string) =>
  +new Date(firstDate) < +new Date(secondDate);
export const isMoreDate = (firstDate: Date | string, secondDate: Date | string) =>
  +new Date(firstDate) > +new Date(secondDate);

export const stringToLocaleDate = (date: string | Date, noYear?: boolean) =>
  new Date(date).toLocaleDateString('ru-RU', { month: 'short', day: 'numeric', year: noYear ? undefined : '2-digit' });

export const toShortDateFormat = (date: string | Date) => dayjs(date).format(shortDateFormat);

export const toDateString = (dateString: string | Date, format?: string, targetFormat = formattedDateFormat) =>
  dayjs(dateString, format).format(targetFormat);

export const getPTHTime = (timeString: string) => {
  const pthTimeString = `PT${timeString.split(':')[0].replace(/^0/, '')}H${timeString.split(':')[1]}M`;
  const hours = dayjs.duration(pthTimeString).hours();
  if (hours < 0 || hours > 23)
    return null;

  const minutes = dayjs.duration(pthTimeString).minutes();
  if (minutes < 0 || minutes > 59)
    return null;
  
  return pthTimeString;
}

export const getTimeFromPTH = (timeString: string) => {
  const time = dayjs.duration(timeString);

  return `${time.hours().toString().padStart(2, '0')}:${time.minutes().toString().padStart(2, '0')}`;
};

/** Returns PT14H00M from PT14H */
export const getNormalizedPTHTime = (pthString: string) => getPTHTime(getTimeFromPTH(pthString));

export const getDatePeriod = (from: Date | string, to: Date | string, noYear?: boolean) =>
  `${stringToLocaleDate(from, noYear)} – ${stringToLocaleDate(to, noYear)}`;

export const getDateWithUtcOffset = (date: Date | string, offset: number) => {
  if (typeof date === 'string') {
    return new Date(+dayjs(date).utcOffset(offset));
  }

  return new Date(+dayjs(getIsoDateWithUtcOffset(date, offset)).utcOffset(offset));
};

export const getIsoDateWithUtcOffset = (date: Date | string, offset: number) =>
  dayjs(date).utcOffset(offset).format(isoFormatDateFormat);

// check if day in date1 is before day in date2
export const isDayBefore = (date1: Date | string, date2: Date | string) => dayjs(date1).isBefore(dayjs(date2), 'day');

export const diffInDays = (date1: Date | string, date2: Date | string) => dayjs(date1).diff(dayjs(date2), 'days');

// return month name in russian
export const getMonthNameWithYear = (date: Date | string) => dayjs(date).format('MMMM YYYY');
